export type DebouncedFunc<T extends (...args: any[]) => any> = {
    (...args: Parameters<T>): ReturnType<T> | undefined;
    cancel(): void;
    flush(): ReturnType<T> | undefined;
};

export type DebouncedFuncLeading<T extends (...args: any[]) => any> = DebouncedFunc<T> & {
    (...args: Parameters<T>): ReturnType<T>;
};

type DebounceOptions = {
    leading?: boolean;
    maxWait?: number;
    trailing?: boolean;
};

export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait = 0,
    options: DebounceOptions = {}
): DebouncedFunc<T> {
    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }

    const settings =
        options && (typeof options === 'object' || typeof options === 'function') ? options : {};
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastArgs: Parameters<T> | undefined;
    let lastThis: unknown;
    let lastCallTime: number | undefined;
    let lastInvokeTime = 0;
    let result: ReturnType<T> | undefined;
    const leading = settings.leading === undefined ? false : Boolean(settings.leading);
    const trailing = settings.trailing === undefined ? true : Boolean(settings.trailing);
    const maxing = settings.maxWait !== undefined;
    const maxWait = maxing ? Math.max(Number(settings.maxWait) || 0, wait) : 0;

    const invokeFunc = (time: number): ReturnType<T> => {
        const args = lastArgs;
        if (args === undefined) {
            throw new Error('Cannot invoke debounce without pending arguments');
        }
        const thisArg = lastThis;
        lastArgs = undefined;
        lastThis = undefined;
        lastInvokeTime = time;
        const invocationResult: ReturnType<T> = func.apply(thisArg, args);
        result = invocationResult;
        return invocationResult;
    };

    const shouldInvoke = (time: number): boolean => {
        const timeSinceLastCall = time - (lastCallTime ?? 0);
        return (
            lastCallTime === undefined ||
            timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxing && time - lastInvokeTime >= maxWait)
        );
    };

    const remainingWait = (time: number): number => {
        const timeSinceLastCall = time - (lastCallTime ?? 0);
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;
        return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting;
    };

    const trailingEdge = (time: number): ReturnType<T> | undefined => {
        timer = undefined;
        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = undefined;
        lastThis = undefined;
        return result;
    };

    const leadingEdge = (time: number): ReturnType<T> | undefined => {
        lastInvokeTime = time;
        timer = setTimeout(timerExpired, Math.max(0, wait));
        return leading ? invokeFunc(time) : result;
    };

    const timerExpired = () => {
        const time = Date.now();
        if (shouldInvoke(time)) {
            trailingEdge(time);
        } else {
            timer = setTimeout(timerExpired, Math.max(0, remainingWait(time)));
        }
    };

    const debounced = function (this: unknown, ...args: Parameters<T>) {
        const time = Date.now();
        const invoking = shouldInvoke(time);
        lastArgs = args;
        lastThis = this;
        lastCallTime = time;

        if (invoking) {
            if (timer === undefined) {
                return leadingEdge(time);
            }
            if (maxing) {
                clearTimeout(timer);
                timer = setTimeout(timerExpired, Math.max(0, wait));
                return invokeFunc(time);
            }
        }
        if (timer === undefined) {
            timer = setTimeout(timerExpired, Math.max(0, wait));
        }
        return result;
    } as DebouncedFunc<T>;

    debounced.cancel = () => {
        if (timer !== undefined) {
            clearTimeout(timer);
        }
        lastInvokeTime = 0;
        lastArgs = undefined;
        lastCallTime = undefined;
        lastThis = undefined;
        timer = undefined;
    };

    debounced.flush = () => (timer === undefined ? result : trailingEdge(Date.now()));

    return debounced;
}

export function throttle<T extends (...args: any[]) => any>(
    func: T,
    wait = 0,
    options: Pick<DebounceOptions, 'leading' | 'trailing'> = {}
): DebouncedFunc<T> {
    const settings =
        options && (typeof options === 'object' || typeof options === 'function') ? options : {};
    return debounce(func, wait, {
        leading: settings.leading === undefined ? true : Boolean(settings.leading),
        trailing: settings.trailing === undefined ? true : Boolean(settings.trailing),
        maxWait: wait,
    });
}
